/** Scales, ballistics and assessments for the meters. Pure functions; unit-tested. */

export type MeterMode = 'sample' | 'truepeak' | 'ppm-ebu' | 'ppm-bbc';

export interface Tick {
  db: number;
  label: string;
}

export interface MeterScale {
  mode: MeterMode;
  name: string;
  unit: string;
  min: number;
  max: number;
  ticks: Tick[];
  /** dBFS from which the bar is drawn amber. */
  warnFrom: number;
  /** dBFS from which the bar is drawn red and the overload indicator lights. */
  overFrom: number;
}

/** Digital alignment level: 0 dBu = -18 dBFS (EBU R68), so PPM 4 / EBU "TEST" = -18 dBFS. */
export const ALIGNMENT_DBFS = -18;

/** BBC PPM marks 1-7: 4 dB per mark, except 6 dB between marks 1 and 2; mark 4 = alignment. */
export const BBC_MARKS: Record<number, number> = { 1: -32, 2: -26, 3: -22, 4: -18, 5: -14, 6: -10, 7: -6 };

export function meterScale(mode: MeterMode, maxTruePeak = -1): MeterScale {
  switch (mode) {
    case 'sample':
    case 'truepeak':
      return {
        mode,
        name: mode === 'sample' ? 'Sample peak' : 'True peak',
        unit: mode === 'sample' ? 'dBFS' : 'dBTP',
        min: -60,
        max: 0,
        ticks: [0, -3, -6, -9, -12, -15, -18, -24, -30, -40, -50, -60].map((db) => ({ db, label: String(db) })),
        warnFrom: -10,
        overFrom: maxTruePeak,
      };
    case 'ppm-ebu':
      return {
        mode,
        name: 'PPM (EBU)',
        unit: 'EBU',
        min: -34,
        max: -4,
        ticks: [-12, -8, -4, 0, 4, 8, 12].map((u) => ({ db: ALIGNMENT_DBFS + u, label: u === 0 ? 'TEST' : u > 0 ? `+${u}` : String(u) })),
        warnFrom: ALIGNMENT_DBFS + 4,
        overFrom: ALIGNMENT_DBFS + 9, // permitted maximum level
      };
    case 'ppm-bbc':
      return {
        mode,
        name: 'PPM (BBC)',
        unit: 'BBC',
        min: -34,
        max: -4,
        ticks: Object.entries(BBC_MARKS).map(([mark, db]) => ({ db, label: mark })),
        warnFrom: BBC_MARKS[5],
        overFrom: BBC_MARKS[6],
      };
  }
}

/**
 * Breakpoints [dB, position] for the digital peak scales: the top 20 dB, where
 * programme peaks and limits sit, gets half the meter height.
 */
const PEAK_SCALE: [number, number][] = [
  [-60, 0],
  [-40, 0.17],
  [-30, 0.32],
  [-20, 0.5],
  [-10, 0.75],
  [0, 1],
];

/** Maps dB to 0..1 along the meter (clamped). Peak scales are piecewise linear, PPM scales linear. */
export function scalePosition(scale: { min: number; max: number; mode?: MeterMode }, db: number | null): number {
  if (db === null || !Number.isFinite(db)) return 0;
  if (scale.mode === 'sample' || scale.mode === 'truepeak') {
    if (db <= PEAK_SCALE[0][0]) return 0;
    for (let i = 1; i < PEAK_SCALE.length; i++) {
      const [d1, p1] = PEAK_SCALE[i];
      if (db <= d1) {
        const [d0, p0] = PEAK_SCALE[i - 1];
        return p0 + ((db - d0) / (d1 - d0)) * (p1 - p0);
      }
    }
    return 1;
  }
  return Math.max(0, Math.min(1, (db - scale.min) / (scale.max - scale.min)));
}

/**
 * Display ballistics for peak bars: instant rise, linear fall, peak hold.
 * IEC 60268-18 digital peak meters return 20 dB in 1.7 s.
 */
export class PeakBallistics {
  value = -Infinity;
  hold = -Infinity;
  private holdUntil = 0;
  private last = 0;

  constructor(
    private readonly fallDbPerSecond = 20 / 1.7,
    private readonly holdMs = 2000,
  ) {}

  /** Advances to time `now` (ms) with the newest reading (dB or null for silence). */
  update(now: number, target: number | null): void {
    const t = target ?? -Infinity;
    const dt = this.last === 0 ? 0 : Math.min(0.25, (now - this.last) / 1000);
    this.last = now;
    if (t >= this.value) this.value = t;
    else this.value = Math.max(t, this.value - this.fallDbPerSecond * dt);
    if (t >= this.hold || now > this.holdUntil) {
      if (t >= this.hold) {
        this.hold = t;
        this.holdUntil = now + this.holdMs;
      } else {
        this.hold = Math.max(this.value, this.hold - this.fallDbPerSecond * dt);
      }
    }
  }

  resetHold(): void {
    this.hold = this.value;
    this.holdUntil = 0;
  }
}

export interface LoudnessTarget {
  id: string;
  label: string;
  integrated: number;
  tolerance: number;
  maxTruePeak: number;
}

/** Loudness target of a preset or profile, for meters and graphs. */
export function targetFromPreset(t: { id: number; name: string; spec: { loudness: { integrated: number; tolerance: number; maxTruePeak: number } | null } }): LoudnessTarget | null {
  if (!t.spec.loudness) return null;
  return { id: String(t.id), label: t.name, integrated: t.spec.loudness.integrated, tolerance: t.spec.loudness.tolerance, maxTruePeak: t.spec.loudness.maxTruePeak };
}

/** Fallback display targets when a stream has no presets selected. */
export const LOUDNESS_TARGETS: LoudnessTarget[] = [
  { id: 'r128', label: 'EBU R128 (-23 LUFS)', integrated: -23, tolerance: 1, maxTruePeak: -1 },
  { id: 'a85', label: 'ATSC A/85 (-24 LKFS)', integrated: -24, tolerance: 2, maxTruePeak: -2 },
  { id: 'online', label: 'Online streaming (-14 LUFS)', integrated: -14, tolerance: 1, maxTruePeak: -1 },
];

export type Level = 'pass' | 'warn' | 'fail' | 'info';

export interface Assessment {
  level: Level;
  text: string;
}

/** Integrated loudness against a target, in plain English with the gain change needed. */
export function assessIntegrated(integrated: number | null, elapsed: number, target: LoudnessTarget): Assessment {
  if (integrated === null) return { level: 'info', text: 'Waiting for programme audio' };
  if (elapsed < 10) return { level: 'info', text: 'Measuring (less than 10 s integrated)' };
  const error = integrated - target.integrated;
  const abs = Math.abs(error);
  if (abs <= target.tolerance) return { level: 'pass', text: `On target (${fmtSigned(error)} LU)` };
  const direction = error > 0 ? 'too loud' : 'too quiet';
  const change = error > 0 ? `reduce by ${abs.toFixed(1)} dB` : `raise by ${abs.toFixed(1)} dB`;
  return { level: abs <= target.tolerance * 2 ? 'warn' : 'fail', text: `${abs.toFixed(1)} LU ${direction}, ${change}` };
}

export function assessTruePeak(maxTruePeak: number | null, target: LoudnessTarget): Assessment {
  if (maxTruePeak === null) return { level: 'info', text: 'No signal' };
  if (maxTruePeak > target.maxTruePeak) {
    return { level: 'fail', text: `Over the ${target.maxTruePeak} dBTP limit by ${(maxTruePeak - target.maxTruePeak).toFixed(1)} dB` };
  }
  return { level: 'pass', text: `Within the ${target.maxTruePeak} dBTP limit` };
}

export function assessCorrelation(c: number | null): Assessment {
  if (c === null) return { level: 'info', text: 'No signal' };
  if (c < -0.3) return { level: 'fail', text: 'Out of phase' };
  if (c < 0) return { level: 'warn', text: 'Wide or partly out of phase' };
  if (c > 0.95) return { level: 'pass', text: 'In phase (near mono)' };
  return { level: 'pass', text: 'In phase' };
}

export function assessBalance(b: number | null): Assessment {
  if (b === null) return { level: 'info', text: 'No signal' };
  const abs = Math.abs(b);
  if (abs < 1) return { level: 'pass', text: 'Centred' };
  const side = b > 0 ? 'Right' : 'Left';
  return { level: abs >= 3 ? 'warn' : 'pass', text: `${side} louder by ${abs.toFixed(1)} dB` };
}

export function assessMono(delta: number | null): Assessment {
  if (delta === null) return { level: 'info', text: 'No signal' };
  if (delta > -3.5) return { level: 'pass', text: 'Good mono compatibility' };
  if (delta > -6) return { level: 'warn', text: 'Some level loss in mono' };
  return { level: 'fail', text: 'Poor mono compatibility' };
}

export function fmtDb(v: number | null | undefined, digits = 1): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '--';
  return v.toFixed(digits);
}

export function fmtSigned(v: number, digits = 1): string {
  const zero = (0).toFixed(digits);
  const s = v.toFixed(digits);
  if (s === zero || s === `-${zero}`) return zero;
  return v > 0 ? `+${s}` : s;
}

export function fmtDuration(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
}
